// filepath: /Users/benedikt/Documents/code/time-tracking/time-snap/src/pages/Projects.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';

/**
 * @typedef {Object} ProjectData
 * @property {string} name - Name des Projekts
 * @property {string|number} hourlyRate - Stundensatz des Projekts
 * @property {string|number} dailyRate - Tagessatz des Projekts
 * @property {string|number} budget - Budget des Projekts
 * @property {string} note - Notiz zum Projekt
 * @property {string} clientId - ID des zugehörigen Kunden
 */

/**
 * @typedef {Object} Project
 * @property {string} _id - Eindeutige ID des Projekts
 * @property {string} name - Name des Projekts
 * @property {number} 'hourly-rate' - Stundensatz des Projekts
 * @property {number} 'daily-rate' - Tagessatz des Projekts
 * @property {number} budget - Budget des Projekts
 * @property {string} note - Notiz zum Projekt
 * @property {string} customer - ID des zugehörigen Kunden
 */

/**
 * @typedef {Object} Client
 * @property {string} _id - Eindeutige ID des Kunden
 * @property {Object} company - Unternehmensdaten des Kunden
 * @property {string} company.name - Name des Unternehmens
 * @property {Array} company.contactPersons - Kontaktpersonen des Unternehmens
 */

/**
 * @typedef {Object} ProjectsProps
 * @property {Function} setLoggedIn - Funktion zum Setzen des Login-Status
 */

/**
 * Projects-Komponente zur Verwaltung von Projekten
 * 
 * @param {ProjectsProps} props - Komponenteneigenschaften
 * @returns {JSX.Element} Projects-Komponente
 */
function Projects({ setLoggedIn }) {
  // Zustandsverwaltung für Dialoge und Daten
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [projectData, setProjectData] = useState({
    name: '',
    hourlyRate: '',
    dailyRate: '',
    budget: '',
    note: '',
    clientId: '',
  });
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Hooks verwenden
  const navigate = useNavigate();
  const { isAuthenticated, validateAndRefreshToken } = useAuth(setLoggedIn);
  const { loading, error, fetchProjects, fetchClients, addProject, updateProject, setError } = useApi();

  /**
   * Überprüft die Authentifizierung des Benutzers
   */
  useEffect(() => {
    validateAndRefreshToken();
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, validateAndRefreshToken, navigate]);

  /**
   * Lädt Projekte und Kunden beim ersten Rendern
   */
  useEffect(() => {
    const loadData = async () => {
      try {
        const projectsData = await fetchProjects();
        setProjects(projectsData);
        
        const clientsData = await fetchClients();
        setClients(clientsData);
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
      }
    };
    
    loadData();
  }, [fetchProjects, fetchClients]);

  /**
   * Aktualisiert die Projektdaten bei Formularänderungen
   * 
   * @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>} e - Das Event-Objekt
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProjectData({
      ...projectData,
      [name]: value
    });
  };

  /**
   * Formatiert die Projektdaten für API-Anfragen
   * 
   * @param {ProjectData} data - Rohe Projektdaten
   * @param {boolean} isEdit - Gibt an, ob es sich um eine Bearbeitung handelt
   * @returns {Object} Formatierte Projektdaten
   */
  const formatProjectData = (data, isEdit = false) => {
    if (isEdit) {
      return {
        name: data.name,
        "hourly-rate": parseInt(data.hourlyRate) || 0,
        "daily-rate": parseInt(data.dailyRate) || 0,
        budget: parseInt(data.budget) || 0,
        note: data.note,
        customer: data.clientId,
      };
    } else {
      return {
        name: data.name,
        hourlyRate: parseInt(data.hourlyRate) || 0,
        dailyRate: parseInt(data.dailyRate) || 0,
        budget: parseInt(data.budget) || 0,
        note: data.note,
        customer: data.clientId,
      };
    }
  };

  /**
   * Fügt ein neues Projekt hinzu
   */
  const handleAddProject = async () => {
    try {
      const formattedData = formatProjectData(projectData);
      await addProject(formattedData);
      setIsDialogOpen(false);
      
      // Projekte neu laden
      const updatedProjects = await fetchProjects();
      setProjects(updatedProjects);
    } catch (err) {
      console.error('Fehler beim Hinzufügen des Projekts:', err);
      setError("Fehler beim Hinzufügen des Projekts");
    }
  };

  /**
   * Öffnet den Dialog zum Bearbeiten eines Projekts
   * 
   * @param {Project} project - Das zu bearbeitende Projekt
   */
  const handleEditProject = (project) => {
    setIsEditing(true);
    setCurrentProjectId(project.project_id || project._id);
    setProjectData({
      name: project.name,
      hourlyRate: project['hourly-rate'] || '',
      dailyRate: project['daily-rate'] || '',
      budget: project.budget || '',
      note: project.note || '',
      clientId: project.clientId || project.customer || '',
    });
    setIsDialogOpen(true);
  };

  /**
   * Aktualisiert ein bestehendes Projekt
   */
  const handleUpdateProject = async () => {
    try {
      const formattedData = formatProjectData(projectData, true);
      await updateProject(currentProjectId, formattedData);
      setIsDialogOpen(false);
      
      // Projekte neu laden
      const updatedProjects = await fetchProjects();
      setProjects(updatedProjects);
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Projekts:', err);
      setError("Fehler beim Aktualisieren des Projekts");
    }
  };

  /**
   * Setzt die Projektdaten auf Standardwerte zurück
   */
  const resetProjectData = () => {
    setProjectData({
      name: '',
      hourlyRate: '',
      dailyRate: '',
      budget: '',
      note: '',
      clientId: '',
    });
  };

  /**
   * Öffnet den Dialog zum Hinzufügen eines neuen Projekts
   */
  const handleOpenAddDialog = () => {
    setIsEditing(false);
    resetProjectData();
    setIsDialogOpen(true);
  };

  /**
   * Rendert den Projektdialog
   * 
   * @returns {JSX.Element} Dialog-Komponente
   */
  const renderProjectDialog = () => {
    if (!isDialogOpen) return null;
    
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-md shadow-md w-full max-w-lg">
          <h2 className="text-xl font-bold mb-4">
            {isEditing ? "Projekt bearbeiten" : "Neues Projekt hinzufügen"}
          </h2>
          {error && <ErrorAlert message={error} />}
          <div className="mb-4">
            <label className="block mb-2">Projektname</label>
            <input
              type="text"
              name="name"
              value={projectData.name}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Kunde</label>
            <select
              name="clientId"
              value={projectData.clientId}
              onChange={handleInputChange}
              className="w-full p-2 h-[42px] border border-gray-300 rounded-md"
            >
              <option value="">-- Kunden auswählen --</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.company.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Stundensatz</label>
            <input
              type="number"
              name="hourlyRate"
              value={projectData.hourlyRate}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Tagessatz</label>
            <input
              type="number"
              name="dailyRate"
              value={projectData.dailyRate}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Budget</label>
            <input
              type="number"
              name="budget"
              value={projectData.budget}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Notiz</label>
            <textarea
              name="note"
              value={projectData.note}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows={3}
            ></textarea>
          </div>
          <div className="flex justify-end">
            <Button
              variant="text"
              onClick={() => setIsDialogOpen(false)}
              className="mr-2"
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={isEditing ? handleUpdateProject : handleAddProject}
            >
              {isEditing ? "Aktualisieren" : "Hinzufügen"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Ermittelt den Kundennamen anhand der Kunden-ID
   * 
   * @param {string} clientId - ID des Kunden
   * @returns {string} Name des Kunden oder leerer String
   */
  const getClientName = (clientId) => {
    const client = clients.find(client => client._id === clientId);
    return client ? client.company.name : '';
  };

  return (
    <div className="px-[90px] py-[112px]">
      <div className="flex justify-between items-center mb-[72px]">
        <h1 className="text-[60px] leading-[63px] font-bold">Standorte</h1>
        <Button
          variant="primary"
          onClick={handleOpenAddDialog}
        >
          Hinzufügen
        </Button>
      </div>
      
      {/* {loading && <LoadingSpinner />} */}
      {error && <ErrorAlert message={error} />}
      
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
          <thead>
            <tr>
              <th className="pt-[64px] py-3 px-4 border-b text-left text-[#B2B2B2]">Name</th>
              {/* <th className="pt-[64px] py-3 px-4 border-b text-left text-[#B2B2B2]">Kunde</th>
              <th className="pt-[64px] py-3 px-4 border-b text-left text-[#B2B2B2]">Stundensatz</th>
              <th className="pt-[64px] py-3 px-4 border-b text-left text-[#B2B2B2]">Tagessatz</th>
              <th className="pt-[64px] py-3 px-4 border-b text-left text-[#B2B2B2]">Budget</th> */}
              <th className="pt-[64px] py-3 px-4 border-b text-left text-[#B2B2B2]"></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project._id}>
                <td className="pb-[64px] py-3 px-4 border-b font-bold">{project.name}</td>
                {/* <td className="pb-[64px] py-3 px-4 border-b">
                  {getClientName(project.customer)}
                </td>
                <td className="pb-[64px] py-3 px-4 border-b">{project['hourly-rate']}</td>
                <td className="pb-[64px] py-3 px-4 border-b">{project['daily-rate']}</td>
                <td className="pb-[64px] py-3 px-4 border-b">{project.budget}</td> */}
                <td className="pb-[64px] py-3 px-8 border-b text-[#0000FF] text-right font-[700]">
                  <Button 
                    variant="text" 
                    onClick={() => handleEditProject(project)} 
                    className="hover:text-[#0000DD]"
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {renderProjectDialog()}
    </div>
  );
}

export default Projects;
